// ================================================
// CONTEÚDO PADRÃO — textos embutidos no build
// ================================================
// Todo o texto editorial que antes estava fixo no JSX.
// É o que o Google enxerga e o que aparece caso a API esteja fora.
//
// Títulos com destaque são divididos em `heading` + `headingEm`
// (o trecho em itálico laranja) em vez de guardar HTML. O painel
// edita texto puro e o React escapa tudo — sem risco de injeção.
//
// Não editar valores aqui esperando mudar o site depois que o
// painel entrar: a partir da Fase 2 o conteúdo vem do banco e
// isto vira apenas o piso de segurança.

export const defaults = {
  // ------------------------------------------------
  // HOME
  // ------------------------------------------------
  'home.hero': {
    metaLeft: '18+ anos de experiência · Ariquemes/RO',
    metaRight: 'Cobertura em todo o Brasil',
    lead:
      'Batimetria, outorgas, perícias, PPCIP e licenciamento ambiental. ' +
      'Profissionais com doutorado, mestrado e especialização atuando com ' +
      'responsabilidade socioambiental em todo o país.',
    ctaPrimary: 'Falar pelo WhatsApp →',
    ctaSecondary: 'Ver serviços',
    scrollHint: 'Role para descobrir',
    image: '/images/batimetria.webp',
  },

  'home.ticker': {
    words: [
      'Batimetria',
      'Outorgas',
      'Estanqueidade',
      'PPCIP',
      'Perícias',
      'PRAD',
      'Pontes & Bueiros',
      'Piscicultura',
    ],
    separator: '✦',
  },

  'home.numbers': {
    stats: [
      { label: 'Anos de experiência dos profissionais', value: '18', suffix: '+' },
      { label: 'Áreas técnicas', value: '07', suffix: '' },
      { label: 'Estados de atuação', value: '05', suffix: '' },
      { label: 'Cidades atendidas', value: '25', suffix: '+' },
    ],
  },

  'home.quote': {
    quote: 'O melhor projeto começa com a leitura honesta do território.',
    author: '— Equipe técnica HidroAtiva · Ariquemes/RO',
    video: '/video/videowhatshidro.mp4',
  },

  'home.portfolio': {
    eyebrow: 'Portfólio técnico',
    heading: 'Frentes',
    headingEm: 'técnicas.',
    paragraph:
      'Soluções completas em recursos hídricos — do estudo de campo ao protocolo, ' +
      'com responsabilidade técnica em cada entrega.',
    cta: 'Ver todos os serviços →',
    // Quantos serviços aparecem em destaque na home
    featuredCount: 3,
  },

  'home.aquicultura': {
    eyebrow: 'Especialidade · piscicultura',
    heading: 'Regularize sua',
    headingEm: 'piscicultura',
    headingAfter: 'com segurança técnica e ambiental.',
    paragraph1:
      'Planejamento, licenciamento ambiental e análise técnica completa para implantação ' +
      'e regularização de tanques, viveiros escavados e demais estruturas de criação de peixes.',
    paragraph2:
      'Outorga de uso da água, estudos hídricos e precisão geoespacial em todas as etapas ' +
      'garantem conformidade legal e sustentabilidade da produção.',
    cta: 'Falar sobre meu projeto →',
    badge1: { icon: '≈', title: 'Precisão geoespacial', value: 'GNSS RTK' },
    badge2: { icon: '⌖', title: 'Etapas cobertas', value: '100%' },
    image: '/images/pisculturaImage.jpeg',
    imageAlt: 'Vista aérea de viveiros de piscicultura',
  },

  'home.partners': {
    heading: 'Empresas parceiras e',
    headingEm: 'clientes.',
  },

  'home.map': {
    eyebrow: 'Área de atuação',
    heading: 'Presentes em',
    headingEm: 'todo o Brasil.',
    lead: 'Passe o mouse ou toque em um estado para ver as cidades de atuação.',
    hint: '↑ Passe o mouse ou toque em um estado',
  },

  // ------------------------------------------------
  // GLOBAL — aparece em várias páginas
  // ------------------------------------------------
  'global.contact': {
    eyebrow: 'Contato',
    heading: 'Vamos',
    headingEm: 'conversar?',
    mapTitle:
      'Localização da HidroAtiva — R. Flôr do Ipê, 2130, Setor 4, Ariquemes/RO',
    mapQuery: 'R. Flôr do Ipê, 2130 - Setor 4, Ariquemes - RO, 76873-474',
    footerRight: 'Serviços e Projetos de Engenharia · Ariquemes/RO',
  },

  // Dados de contato. Aparecem em toda página e no schema.org.
  // São dois WhatsApp, atendendo canais diferentes.
  'global.dados': {
    whatsappComercial: '556999805556',
    whatsappComercialLabel: '(69) 9 9980-5556',
    whatsappAdmin: '5569993654280',
    whatsappAdminLabel: '(69) 9 9365-4280',
    whatsappMensagem:
      'Olá! Vim pelo site da HidroAtiva e gostaria de mais informações sobre os serviços',
    emails: [
      'adm@hidroativa.com.br',
      'contato@hidroativa.com.br',
      'servicos@hidroativa.com.br',
    ],
    enderecoRua: 'R. Flôr do Ipê, 2130 — St. 4',
    enderecoCidade: 'Ariquemes/RO',
    enderecoCep: '76873-474',
    instagram: 'https://www.instagram.com/hidroonorte/',
    facebook: 'https://www.facebook.com/share/17Tpdk3JJA/',
    cnpj: '49.348.546/0001-27',
  },

  // Perguntas frequentes exibidas em /faq
  'data.faqs': {
    itens: [
      {
        q: 'Minha empresa precisa de Licença Ambiental para funcionar?',
        a: 'Muitas atividades precisam de regularização ambiental para evitar multas, embargos e problemas com os órgãos fiscalizadores.',
      },
      {
        q: 'O Alvará do Corpo de Bombeiros é obrigatório?',
        a: 'Sim. O documento é essencial para garantir a segurança do imóvel e o funcionamento regular da empresa perante a fiscalização.',
      },
      {
        q: 'Posso fazer uso do fogo controlado sem autorização?',
        a: 'Não. É necessário possuir autorização ambiental específica para realizar queimadas controladas de forma legal e segura.',
      },
      {
        q: 'O que é Outorga de Uso da Água e quando preciso dela?',
        a: 'A outorga é a autorização necessária para utilização de recursos hídricos, como poços, captação de água e atividades que dependem do uso da água para sua operação.',
      },
    ],
  },

  // Logos do carrossel de parceiros
  'data.partners': {
    itens: [
      { name: 'Canaã Geração de Energia Renovável S.A.', logo: '/partners/canaa_logo.png' },
      { name: 'White Solder Metalurgia e Mineração', logo: '/partners/white-solder-logo.jpg' },
      { name: 'Cooperativa de Produtores de Estanho do Brasil — COOPERTIN', logo: '/partners/coopertin_logo.png' },
      { name: 'Grupo Aegea', logo: '/partners/aegea.svg' },
      { name: 'Madeflona Industrial Madeireira Ltda.', logo: '/partners/madeflona.webp' },
      { name: 'Tinkt International Trade Comércio Ltda.', logo: '/partners/logoTINKT.png' },
      { name: 'Pampa Soluções Agrícolas Ltda.', logo: '/partners/MF-logo-Header.png', caption: 'Pampa Soluções Agrícolas' },
      { name: 'MB Limpeza Urbana Porto Velho', logo: '/partners/logo-mblimp.png' },
      { name: 'Supremax Nutrição Animal', logo: '/partners/supremax.webp' },
      { name: 'Norte Brasil Transmissora de Energia S.A.', logo: '/partners/evoltz.png', caption: 'Norte Brasil Transmissora de Energia S.A.' },
      { name: 'Grupo Avistar', logo: '/partners/avistar_logo.png', caption: 'Grupo Avistar' },
      { name: 'Arla Norte', logo: '/partners/arla-norte.webp' },
      { name: 'Conclue Serviços de Gestão', logo: '/partners/conclue_logo.png', scale: 1.3 },
      { name: 'Atenas Soluções Ambientais', logo: '/partners/atenas_logo.png', scale: 1.3 },
      { name: 'RIOG Engenharia', logo: '/partners/logoBRF.jpeg' },
      { name: 'Cooperativa Coopersanta', logo: '/partners/coopsanta_logo.svg' },
      { name: 'Cooperativa Copermental', logo: '/partners/coopmetal_logo.svg' },
    ],
  },

  // Cidades atendidas, por estado. As coordenadas posicionam os
  // pontos no mapa SVG — ver comentário em BrazilMap.jsx.
  'data.mapa': {
    estados: [
      {
        uf: 'RO',
        label: 'Rondônia',
        desc: 'Estado-sede da HidroAtiva',
        cidades: [
          { n: 'Ariquemes', lon: -63.04, lat: -9.91, hq: true },
          { n: 'Porto Velho', lon: -63.0, lat: -9.3 },
          { n: 'Candeias do Jamari', lon: -62.5, lat: -9.1 },
          { n: 'Itapuã do Oeste', lon: -63.32, lat: -9.55 },
          { n: 'Rio Crespo', lon: -63.2, lat: -9.69 },
          { n: "Machadinho D'Oeste", lon: -62.01, lat: -9.39 },
          { n: 'Cujubim', lon: -62.54, lat: -9.36 },
          { n: 'Alto Paraíso', lon: -63.32, lat: -9.99 },
          { n: 'Monte Negro', lon: -63.29, lat: -10.25 },
          { n: 'Campo Novo de Rondônia', lon: -63.67, lat: -10.57 },
          { n: 'Buritis', lon: -63.4, lat: -10.25 },
          { n: 'Cacaulândia', lon: -62.9, lat: -10.28 },
          { n: 'Jaru', lon: -62.47, lat: -10.44 },
          { n: 'Ji-Paraná', lon: -61.95, lat: -10.88 },
          { n: 'Pres. Médici', lon: -61.9, lat: -11.17 },
          { n: 'Pimenta Bueno', lon: -61.19, lat: -11.67 },
          { n: 'Rolim de Moura', lon: -61.78, lat: -11.73 },
          { n: 'Cabixi', lon: -61.0, lat: -13.0 },
        ],
      },
      {
        uf: 'AM',
        label: 'Amazonas',
        desc: '',
        cidades: [
          { n: 'Manicoré', lon: -70, lat: -5.81 },
          { n: 'Apuí', lon: -59.89, lat: -7.2 },
          { n: 'Boca do Acre', lon: -67.39, lat: -8.76 },
          { n: 'Novo Aripuanã', lon: -60.38, lat: -5.12 },
        ],
      },
      { uf: 'PA', label: 'Pará', desc: '', cidades: [{ n: 'Itaituba', lon: -55.98, lat: -4.28 }] },
      { uf: 'MT', label: 'Mato Grosso', desc: '', cidades: [{ n: 'Lambari do Oeste', lon: -57.97, lat: -15.32 }] },
      { uf: 'BA', label: 'Bahia', desc: '', cidades: [{ n: 'Mascote', lon: -39, lat: -15.55 }] },
    ],
  },

  'global.nav': {
    links: [
      { label: 'Nossos Serviços', to: '/servicos' },
      { label: 'A empresa', to: '/sobre' },
      { label: 'FAQ', to: '/faq' },
    ],
    contactLabel: 'Contato',
    cta: 'Iniciar projeto',
  },

  // ------------------------------------------------
  // PÁGINAS
  // ------------------------------------------------
  'page.home': {
    seo: {
      title:
        'HidroAtiva Engenharia — Recursos Hídricos, Licenciamento Ambiental e Topografia',
      description:
        'HidroAtiva Engenharia: outorga de água, batimetria, licenciamento ambiental, ' +
        'topografia, fotogrametria com drone/RTK, PPCI, PRAD e perícias. Ariquemes/RO, ' +
        'atuação em todo o Brasil.',
      canonical: 'https://hidroativa.com.br/',
    },
  },

  'page.servicos': {
    eyebrow: 'Portfólio técnico',
    heading: 'Nossos',
    headingEm: 'Serviços.',
    lead:
      'Atuação multidisciplinar em recursos hídricos, licenciamento ambiental, ' +
      'topografia, fotogrametria e regularização — cada entrega com responsável ' +
      'habilitado, ART recolhida e relatório apto para protocolo nos órgãos competentes.',
    cta: 'Solicite um orçamento →',
    seo: {
      title: 'Nossos Serviços — HidroAtiva Engenharia',
      description:
        'Conheça os serviços da HidroAtiva: outorga de água, batimetria, licenciamento ' +
        'ambiental, topografia, fotogrametria com drone/RTK, PPCI, PRAD, CAR, EIV, PGRCC, ' +
        'PGRSS, perícias e mais.',
      canonical: 'https://hidroativa.com.br/servicos',
    },
  },

  'page.faq': {
    eyebrow: 'Tire suas dúvidas',
    heading: 'Perguntas',
    headingEm: 'Frequentes.',
    lead:
      'Reunimos as dúvidas mais comuns sobre regularização ambiental, ' +
      'licenciamento e uso de recursos hídricos. Não encontrou o que procurava? ' +
      'Fale com a nossa equipe.',
    seo: {
      title: 'Perguntas Frequentes — HidroAtiva Engenharia',
      description:
        'Dúvidas frequentes sobre Licença Ambiental, Alvará do Corpo de Bombeiros, ' +
        'autorização de fogo controlado e Outorga de Uso da Água. Tire suas dúvidas ' +
        'com a HidroAtiva.',
      canonical: 'https://hidroativa.com.br/faq',
    },
  },

  'page.sobre': {
    eyebrow: 'A empresa',
    heading: 'Sobre a',
    headingEm: 'HidroAtiva',
    lead:
      'A empresa HidroAtiva atua em todo o Brasil. Profissionais com diferentes formações ' +
      'colaboram na melhoria contínua para melhor lhe atender. Além da multidisciplinaridade, ' +
      'os profissionais possuem experiência comprovada, títulos de doutorado, mestrado e especialização.',
    cta: 'Entre em contato conosco →',
    video: '/video/vidroherohidro.mp4',

    stats: [
      { label: 'Anos de experiência', value: '18+' },
      { label: 'Áreas técnicas', value: '07' },
      { label: 'Estados de atuação', value: '05' },
      { label: 'Cidades atendidas', value: '25+' },
    ],

    missaoVisaoValores: [
      {
        icon: '💡',
        title: 'Visão',
        text: 'Ser referência em soluções técnicas no desenvolvimento sustentável em todo o Brasil.',
      },
      {
        icon: '🎯',
        title: 'Missão',
        text:
          'Atuar com responsabilidade socioambiental no fornecimento de serviços técnicos em ' +
          'recursos hídricos, regularização ambiental, sanitária e regularização junto ao ' +
          'Corpo de Bombeiros Militar.',
      },
      {
        icon: '⭐',
        title: 'Nossos Valores',
        text:
          'Honestidade acima de tudo; Equipe unida e treinamento constante; Tratamos todos ' +
          'com seriedade; Confidencialidade e sigilo; Criatividade e inovação.',
      },
    ],

    valoresTitle: 'Valores que nos guiam',
    valores: [
      {
        icon: '✦',
        title: 'Honestidade',
        desc: 'Honestidade acima de tudo — transparência em prazos, custos e viabilidade real.',
        gradient: 'linear-gradient(135deg,var(--orange),var(--coral))',
      },
      {
        icon: '⌬',
        title: 'Equipe unida',
        desc:
          'Equipe unida e treinamento constante — profissionais de diferentes formações ' +
          'que se complementam em cada projeto.',
        gradient: 'linear-gradient(135deg,var(--ocean),var(--deep))',
      },
      {
        icon: '◈',
        title: 'Confidencialidade',
        desc: 'Tratamos todos com seriedade — sigilo contratual garantido em todas as etapas do projeto.',
        gradient: 'linear-gradient(135deg,var(--mint),#4FA77F)',
      },
      {
        icon: '≈',
        title: 'Criatividade e inovação',
        desc:
          'GNSS RTK, sonar batimétrico e geoprocessamento avançado integrados ao rigor ' +
          'metodológico de cada serviço.',
        gradient: 'linear-gradient(135deg,var(--amber),var(--orange))',
      },
    ],

    teamTitle: 'Capacitação técnica',
    team: [
      {
        name: 'Equipe Técnica',
        role: 'Doutores & Mestres',
        icon: '⌬',
        desc: 'Profissionais com títulos de doutorado e mestrado em recursos hídricos, engenharia e geoprocessamento.',
      },
      {
        name: 'Especialistas Campo',
        role: 'GNSS RTK & Sonar',
        icon: '≈',
        desc: 'Instrumentação de ponta para levantamentos batimétricos e geoespaciais com precisão centimétrica.',
      },
      {
        name: 'Regularização',
        role: 'Ambiental & Sanitária',
        icon: '◈',
        desc: 'Expertise em processos junto à SEDAM, órgãos ambientais estaduais e Corpo de Bombeiros Militar.',
      },
    ],

    seo: {
      title: 'A Empresa — HidroAtiva Engenharia',
      description:
        'Conheça a HidroAtiva Engenharia: equipe multidisciplinar com doutores, mestres ' +
        'e especialistas em recursos hídricos, regularização ambiental, sanitária e ' +
        'bombeiros militares.',
      canonical: 'https://hidroativa.com.br/sobre',
    },
  },
};
